import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useLoadScript } from '@react-google-maps/api'
import PropTypes from 'prop-types'
import { TextField } from '@mui/material'

const libraries = ['places']

// Add styles to head to ensure pac-container is always visible
const style = document.createElement('style')
style.textContent = `
  .pac-container {
    z-index: 9999 !important;
    position: fixed !important;
    background-color: white !important;
    border: 1px solid #d9d9d9 !important;
    border-radius: 4px !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3) !important;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial !important;
    margin-top: 2px !important;
  }
  .pac-item {
    padding: 8px !important;
    cursor: pointer !important;
  }
  .pac-item:hover {
    background-color: #f5f5f5 !important;
  }
`
document.head.appendChild(style)

const PlacesAutocomplete = ({ onPlaceSelect, index, disabled, countries }) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
    libraries,
  })

  const [searchValue, setSearchValue] = useState('')
  const inputRef = useRef(null)
  const autocompleteRef = useRef(null)
  const sessionTokenRef = useRef(null)

  // Create a new session token when component mounts or after a selection
  const createSessionToken = useCallback(() => {
    if (window.google) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken()
    }
  }, [])

  const getCityComponent = addressComponents => {
    // Try to find the city in this order: locality -> sublocality -> neighborhood
    for (const component of addressComponents) {
      // Check all types for the component
      if (component.types.includes('locality')) {
        return component.short_name
      }
    }

    // If no locality found, look for sublocality
    for (const component of addressComponents) {
      if (component.types.includes('sublocality')) {
        return component.short_name
      }
    }

    // If still no city found, try neighborhood
    for (const component of addressComponents) {
      if (component.types.includes('neighborhood')) {
        return component.short_name
      }
    }

    return ''
  }

  useEffect(() => {
    if (isLoaded && inputRef.current && !disabled) {
      createSessionToken()

      // Get the input element from the TextField
      const input = inputRef.current.querySelector('input')

      autocompleteRef.current = new window.google.maps.places.Autocomplete(input, {
        types: ['address'],
        componentRestrictions: { country: countries },
        fields: ['address_components', 'formatted_address', 'geometry'],
        sessionToken: sessionTokenRef.current,
        minLength: 5,
        strictBounds: false,
      })

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace()

        if (!place.address_components) {
          return
        }

        // Extract address components
        const addressComponents = {
          street_number: '',
          route: '',
          locality: '',
          administrative_area_level_1: '',
          postal_code: '',
          formatted_address: place.formatted_address || '',
        }

        place.address_components.forEach(component => {
          const type = component.types[0]
          if (Object.prototype.hasOwnProperty.call(addressComponents, type)) {
            addressComponents[type] = component.short_name
          }
        })

        // Get latitude and longitude
        const lat = place.geometry?.location?.lat()
        const lng = place.geometry?.location?.lng()

        // Get city using the new helper function
        const city = getCityComponent(place.address_components)

        // Format the address data
        const addressData = {
          address1: `${addressComponents.street_number} ${addressComponents.route}`.trim(),
          city: city || addressComponents.locality, // Use the found city or fallback to locality
          state: addressComponents.administrative_area_level_1,
          zip: addressComponents.postal_code,
          coordinates: { lat, lng },
        }

        setSearchValue(addressComponents.formatted_address || place.name)
        onPlaceSelect(addressData, index)

        // Create new session token after selection
        createSessionToken()
      })

      return () => {
        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current)
        }
      }
    }
  }, [isLoaded, onPlaceSelect, index, createSessionToken, disabled])

  if (!isLoaded) {
    return <div>Loading...</div>
  }

  return (
    <TextField
      ref={inputRef}
      fullWidth
      label="Search Address"
      variant="outlined"
      value={searchValue}
      onChange={e => {
        setSearchValue(e.target.value)
      }}
      placeholder="Enter a location"
      disabled={disabled}
    />
  )
}

PlacesAutocomplete.propTypes = {
  onPlaceSelect: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  countries: PropTypes.array,
}

PlacesAutocomplete.defaultProps = {
  disabled: false,
  countries: ['US', 'CA'],
}

export default PlacesAutocomplete
